import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
  useComputedColorScheme,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';
import React from 'react';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconCheck,
  IconCircleDashed,
  IconFileText,
  IconUpload,
  IconX,
} from '@tabler/icons-react';

import { buttonTextColor } from 'utils/buttonUtils';

import { BulkProviderIngestJob, BulkProviderIngestResult } from 'api/resource';

interface ProviderFileUploadModalProps {
  uploadModal: boolean;
  setUploadModal: (value: boolean) => void;
  openRef: React.ForwardedRef<() => void | undefined>;
  providerFile: File[];
  setProviderFile: (value: File[]) => void;
  handleSubmitFile: () => void;
  uploadResult: BulkProviderIngestResult | null;
  uploadJob: BulkProviderIngestJob | null;
  isUploading: boolean;
}

export const ProviderFileUploadModal: React.FC<
  ProviderFileUploadModalProps
> = ({
  uploadModal,
  setUploadModal,
  openRef,
  providerFile,
  setProviderFile,
  handleSubmitFile,
  uploadResult,
  uploadJob,
  isUploading,
}) => {
  const computedColorScheme = useComputedColorScheme('dark');
  const [detailsOpen, { toggle: toggleDetails }] = useDisclosure(
    Boolean(uploadResult?.failedCount)
  );
  const [expandedSections, setExpandedSections] = React.useState<
    Record<string, boolean>
  >({});
  const selectedFileName = providerFile?.[0]?.name ?? '';
  const hasInvalidFileType =
    providerFile?.length === 1 &&
    !selectedFileName.toLowerCase().endsWith('.txt');
  const canSubmit =
    providerFile?.length === 1 && !hasInvalidFileType && !isUploading;
  const hasInvalidSelection = providerFile?.length > 1;

  const resultSections = [
    {
      key: 'created',
      title: 'Created',
      color: 'green',
      items: uploadResult?.created ?? [],
      count: uploadResult?.createdCount ?? 0,
      sampleSize: 5,
      render: (provider: BulkProviderIngestResult['created'][number]) =>
        `Row ${provider.row}: created ${provider.license} - ${provider.firstName} ${provider.lastName}`,
    },
    {
      key: 'skipped',
      title: 'Skipped',
      color: 'blue',
      items: uploadResult?.skipped ?? [],
      count: uploadResult?.skippedCount ?? 0,
      sampleSize: 5,
      render: (provider: BulkProviderIngestResult['skipped'][number]) =>
        `Row ${provider.row}: skipped ${provider.license || 'blank row'} (${
          provider.reason
        })`,
    },
    {
      key: 'failed',
      title: 'Failed',
      color: 'red',
      items: uploadResult?.failed ?? [],
      count: uploadResult?.failedCount ?? 0,
      sampleSize: 25,
      render: (provider: BulkProviderIngestResult['failed'][number]) =>
        `Row ${provider.row}: failed${
          provider.license ? ` ${provider.license}` : ''
        } - ${provider.reason}`,
    },
  ];

  return (
    <Modal
      opened={uploadModal}
      onClose={() => setUploadModal(false)}
      size="lg"
      padding="lg"
      title="Bulk Upload Providers"
      data-testid="upload-provider-modal"
    >
      <Stack gap="md">
        <Dropzone
          openRef={openRef}
          activateOnClick={true}
          accept={['text/plain']}
          multiple={false}
          onDrop={(files) => setProviderFile(files)}
          loading={isUploading}
          radius="md"
          p="xl"
          style={{}}
        >
          <Stack align="center" gap="sm">
            <Dropzone.Accept>
              <ThemeIcon size={56} radius="xl" color="green" variant="light">
                <IconCheck size={30} stroke={1.7} />
              </ThemeIcon>
            </Dropzone.Accept>
            <Dropzone.Reject>
              <ThemeIcon size={56} radius="xl" color="red" variant="light">
                <IconX size={30} stroke={1.7} />
              </ThemeIcon>
            </Dropzone.Reject>
            <Dropzone.Idle>
              <ThemeIcon size={56} radius="xl" color="blue" variant="light">
                <IconUpload size={30} stroke={1.7} />
              </ThemeIcon>
            </Dropzone.Idle>
            <Stack align="center" gap={4}>
              <Text fw={600}>Drop a provider registry text file here</Text>
              <Text size="sm" c="dimmed">
                Only one .txt file can be imported at a time.
              </Text>
            </Stack>
          </Stack>
        </Dropzone>

        <Card withBorder radius="md" p="sm">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon
              variant="light"
              color={selectedFileName ? 'blue' : 'gray'}
            >
              <IconFileText size={18} />
            </ThemeIcon>
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Text size="sm" fw={500} truncate>
                {selectedFileName || 'No file selected'}
              </Text>
              <Text size="xs" c="dimmed">
                Provider imports run in the background after upload.
              </Text>
            </Box>
          </Group>
        </Card>

        {hasInvalidSelection && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            Select exactly one provider text file.
          </Alert>
        )}
        {hasInvalidFileType && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            Provider upload requires a .txt file.
          </Alert>
        )}
        {uploadJob && !uploadResult && (
          <Alert
            color="blue"
            icon={<IconCircleDashed size={16} />}
            data-testid="provider-upload-job-status"
          >
            Provider upload is {uploadJob.status.toLowerCase()}.
          </Alert>
        )}
        {uploadResult && (
          <Stack gap="md" data-testid="provider-upload-result">
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
              <Card withBorder radius="md" p="sm">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Created {uploadResult.createdCount}
                </Text>
                <Text fw={700} size="xl" c="green">
                  {uploadResult.createdCount}
                </Text>
              </Card>
              <Card withBorder radius="md" p="sm">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Skipped {uploadResult.skippedCount}
                </Text>
                <Text fw={700} size="xl" c="blue">
                  {uploadResult.skippedCount}
                </Text>
              </Card>
              <Card withBorder radius="md" p="sm">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Failed {uploadResult.failedCount}
                </Text>
                <Text
                  fw={700}
                  size="xl"
                  c={uploadResult.failedCount ? 'red' : 'gray'}
                >
                  {uploadResult.failedCount}
                </Text>
              </Card>
              <Card withBorder radius="md" p="sm">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Rows
                </Text>
                <Text fw={700} size="xl">
                  {uploadResult.totalRows}
                </Text>
              </Card>
            </SimpleGrid>
            <Divider />
            <Card withBorder radius="md" p="sm">
              <UnstyledButton onClick={toggleDetails} w="100%">
                <Group justify="space-between" wrap="nowrap">
                  <Box>
                    <Text size="sm" fw={600}>
                      Row details
                    </Text>
                    <Text size="xs" c="dimmed">
                      Showing a small sample so large imports stay readable.
                    </Text>
                  </Box>
                  <Group gap={6} wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      {detailsOpen ? 'Hide' : 'Show'}
                    </Text>
                    {detailsOpen ? (
                      <IconChevronDown size={16} />
                    ) : (
                      <IconChevronRight size={16} />
                    )}
                  </Group>
                </Group>
              </UnstyledButton>
              <Collapse in={detailsOpen}>
                <Divider my="sm" />
                <ScrollArea.Autosize mah={260}>
                  <Stack gap="sm">
                    {resultSections.map((section) => {
                      const isExpanded = expandedSections[section.key];
                      const visibleItems = isExpanded
                        ? section.items
                        : section.items.slice(0, section.sampleSize);
                      const hiddenCount = Math.max(
                        section.count - visibleItems.length,
                        0
                      );

                      return section.count ? (
                        <Card withBorder radius="md" p="sm" key={section.key}>
                          <Stack gap={6}>
                            <Group justify="space-between">
                              <Text
                                size="xs"
                                c={section.color}
                                fw={700}
                                tt="uppercase"
                              >
                                {section.title}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {section.count} rows
                              </Text>
                            </Group>
                            {section.count > section.sampleSize &&
                              section.key !== 'failed' && (
                                <Button
                                  size="compact-xs"
                                  variant="subtle"
                                  color={section.color}
                                  onClick={() =>
                                    setExpandedSections((current) => ({
                                      ...current,
                                      [section.key]: !isExpanded,
                                    }))
                                  }
                                >
                                  {isExpanded ? 'Show sample' : 'Show all'}
                                </Button>
                              )}
                            {visibleItems.map((provider) => (
                              <Text
                                size="sm"
                                c={section.key === 'failed' ? 'red' : undefined}
                                key={`${section.key}-${provider.row}`}
                              >
                                {section.render(provider as never)}
                              </Text>
                            ))}
                            {hiddenCount > 0 && (
                              <Text size="xs" c="dimmed">
                                {hiddenCount} more {section.title.toLowerCase()}{' '}
                                rows not shown.
                              </Text>
                            )}
                          </Stack>
                        </Card>
                      ) : null;
                    })}
                  </Stack>
                </ScrollArea.Autosize>
              </Collapse>
            </Card>
          </Stack>
        )}

        <Group justify="flex-end">
          {uploadResult ? (
            <Button onClick={() => setUploadModal(false)}>Done</Button>
          ) : (
            <>
              <Button variant="subtle" onClick={() => setUploadModal(false)}>
                Cancel
              </Button>
              <Button
                leftSection={<IconUpload size={16} />}
                onClick={() => handleSubmitFile()}
                disabled={!canSubmit}
                loading={isUploading}
                c={buttonTextColor(computedColorScheme, isUploading)}
              >
                Submit
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};
